import type {
  Account,
  CurrencyCode,
  DashboardWindow,
  ForecastRecord,
  LedgerState,
  OwnershipTag,
  ParsedTransactionCandidate,
  TimelineFilter,
  TransactionKind,
  TransactionLineItem,
  TransactionRecord,
} from "./types";

export const STORAGE_KEY = "clearspend.pwa.ledger.v1";

const nowIso = () => new Date().toISOString();
const id = () => crypto.randomUUID();

export const exchangeRatesToPHP: Record<CurrencyCode, number> = {
  PHP: 1,
  CNY: 7.85,
  USD: 58.6,
  USDT: 58.6,
};

export function convert(amount: number, currency: CurrencyCode) {
  const php = amount * exchangeRatesToPHP[currency];
  return {
    php,
    cny: php / exchangeRatesToPHP.CNY,
  };
}

export function createInitialLedger(): LedgerState {
  const stamp = nowIso();
  const accounts: Account[] = [
    { id: id(), name: "BPI Debit", kind: "bankDebit", currency: "PHP", balance: 0, isActive: true, updatedAt: stamp },
    { id: id(), name: "BPI Savings", kind: "bankSavings", currency: "PHP", balance: 0, isActive: true, updatedAt: stamp },
    { id: id(), name: "GCash", kind: "eWallet", currency: "PHP", balance: 0, isActive: true, updatedAt: stamp },
    { id: id(), name: "Binance", kind: "exchange", currency: "USDT", balance: 0, isActive: true, updatedAt: stamp },
    { id: id(), name: "随身现金", kind: "cash", currency: "PHP", balance: 0, isActive: true, updatedAt: stamp },
    { id: id(), name: "保险箱现金", kind: "cash", currency: "PHP", balance: 0, isActive: true, updatedAt: stamp },
  ];

  return {
    accounts,
    transactions: [],
    forecasts: [],
    audits: [
      {
        id: id(),
        happenedAt: stamp,
        action: "ledger_seeded",
        payload: "{}",
      },
    ],
  };
}

export function loadLedger(): LedgerState {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return createInitialLedger();

  try {
    return stripLegacySecrets(JSON.parse(raw) as LedgerState & { openRouterKey?: string });
  } catch {
    return createInitialLedger();
  }
}

export function saveLedger(state: LedgerState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stripLegacySecrets(state as LedgerState & { openRouterKey?: string })));
}

function stripLegacySecrets(state: LedgerState & { openRouterKey?: string }): LedgerState {
  const { openRouterKey: _discarded, ...safeState } = state;
  return safeState;
}

export function makeTransaction(
  candidate: ParsedTransactionCandidate,
  accountId: string,
  source: TransactionRecord["source"],
): TransactionRecord {
  const converted = convert(candidate.amountOriginal, candidate.currency);
  const lineItems: TransactionLineItem[] = candidate.lineItems.map((item) => {
    const lineConverted = convert(item.priceOriginal, candidate.currency);
    return {
      id: id(),
      ...item,
      currency: candidate.currency,
      pricePHP: lineConverted.php,
      priceCNY: lineConverted.cny,
    };
  });

  const stamp = nowIso();
  return {
    id: id(),
    createdAt: stamp,
    updatedAt: stamp,
    happenedAt: candidate.happenedAt,
    kind: candidate.kind,
    source,
    merchantName: candidate.merchantName,
    merchantBranch: candidate.merchantBranch,
    note: candidate.note,
    amountOriginal: candidate.amountOriginal,
    currencyOriginal: candidate.currency,
    amountPHP: converted.php,
    amountCNY: converted.cny,
    confidence: candidate.confidence,
    pendingReview: candidate.confidence < 0.8,
    ownershipTags: candidate.ownershipTags,
    accountId,
    lineItems,
  };
}

export function applyTransactionBalance(accounts: Account[], transaction: TransactionRecord, direction: 1 | -1) {
  return accounts.map((account) => {
    if (account.id !== transaction.accountId && account.id !== transaction.toAccountId) return account;

    let delta = 0;
    if (transaction.kind === "income" || transaction.kind === "refund") delta = transaction.amountPHP;
    if (transaction.kind === "expense" || transaction.kind === "investment") delta = -transaction.amountPHP;
    if (transaction.kind === "adjustment") delta = transaction.amountPHP;
    if (transaction.kind === "transfer") {
      if (account.id === transaction.accountId) delta = -transaction.amountPHP;
      if (account.id === transaction.toAccountId) delta = transaction.amountPHP;
    }

    return {
      ...account,
      balance: account.balance + delta * direction,
      updatedAt: nowIso(),
    };
  });
}

export function parseNaturalLanguage(input: string): ParsedTransactionCandidate | null {
  const text = input.trim();
  if (!text) return null;

  const amount = extractAmount(text);
  if (!amount) return null;

  const lower = text.toLowerCase();
  const kind: TransactionKind = /转账|转到|转给|transfer/.test(text)
    ? "transfer"
    : /退款|退回|refund/.test(text)
      ? "refund"
      : /收入|入账|收到|到账|airbnb|salary|工资/.test(lower)
        ? "income"
        : /借给|借出|借入|loan/.test(lower)
          ? "loan"
          : /买币|卖币|usdt|binance|coins\.ph/.test(lower)
            ? "investment"
            : "expense";

  const merchant = extractMerchant(text);
  const items = extractLineItems(text, amount.value);
  const ownershipTags = extractOwnershipTags(text);
  const happenedAt = parseTimeExpression(text);
  const confidenceParts = [
    amount.value > 0 ? 0.35 : 0,
    merchant.name !== "未识别商户" ? 0.25 : 0,
    items.length > 0 ? 0.15 : 0,
    ownershipTags.length > 0 ? 0.1 : 0.04,
    /刚刚|今天|昨晚|昨天|前天|\d/.test(text) ? 0.1 : 0.06,
  ];

  return {
    happenedAt,
    kind,
    merchantName: merchant.name,
    merchantBranch: merchant.branch,
    note: text,
    amountOriginal: amount.value,
    currency: amount.currency,
    confidence: Math.min(0.96, confidenceParts.reduce((sum, item) => sum + item, 0)),
    ownershipTags,
    lineItems: items,
  };
}

function extractAmount(text: string): { value: number; currency: CurrencyCode } | null {
  const currency: CurrencyCode = /\$|usd|美元|美金/i.test(text)
    ? "USD"
    : /¥|cny|人民币|块人民币|元/i.test(text)
      ? "CNY"
      : /usdt/i.test(text)
        ? "USDT"
        : "PHP";

  const symbolMatch = text.match(/(?:₱|\$|¥)\s?(\d+(?:[,.]\d{3})*(?:\.\d+)?)/);
  const actionMatch = text.match(/(?:花了|花|消费|付了|付|花费|收入|收到|到账|spent|cost)\s?(\d+(?:[,.]\d{3})*(?:\.\d+)?)/i);
  const unitMatch = text.match(/(\d+(?:[,.]\d{3})*(?:\.\d+)?)\s?(?:php|peso|比索|块钱|块|元|美元|美金|人民币|usdt)/i);
  const raw = symbolMatch?.[1] ?? actionMatch?.[1] ?? unitMatch?.[1];
  if (!raw) return null;
  return { value: Number(raw.replace(/,/g, "")), currency };
}

function extractMerchant(text: string) {
  const seven = /7-?eleven|711/i.test(text);
  if (seven) return { name: "7-Eleven", branch: /楼下|家楼下/.test(text) ? "家楼下" : "" };

  const atMatch = text.match(/(?:在|从|去|到)\s?([A-Za-z0-9\u4e00-\u9fa5 .'-]{2,24}?)(?:买|花|消费|吃|做|付|收到|转|，|,|$)/);
  const known = ["IKEA", "宜家", "OpenAI", "ChatGPT", "Airbnb", "GCash", "BPI", "Binance", "Coins.ph", "Setapp"];
  const hit = known.find((name) => text.toLowerCase().includes(name.toLowerCase()));
  if (hit) return { name: hit === "宜家" ? "IKEA" : hit, branch: "" };
  if (atMatch?.[1]) return { name: atMatch[1].trim(), branch: "" };
  return { name: "未识别商户", branch: "" };
}

function extractLineItems(text: string, total: number) {
  const candidates = [
    { test: /水|矿泉水|瓶装水|纯净水/i, original: "瓶装水", normalized: "水", category: "grocery", unit: "瓶" },
    { test: /药|药品|medicine|pharmacy/i, original: "药品", normalized: "药品", category: "医疗健康", unit: "件" },
    { test: /咖啡|coffee/i, original: "咖啡", normalized: "咖啡", category: "餐饮", unit: "杯" },
    { test: /饭|餐|外卖|restaurant|dinner|lunch/i, original: "餐饮", normalized: "外出就餐", category: "餐饮", unit: "次" },
    { test: /openai|chatgpt|api/i, original: "OpenAI 订阅", normalized: "AI 服务订阅", category: "subscription", unit: "项" },
    { test: /airbnb/i, original: "Airbnb 入账", normalized: "Airbnb 收入", category: "income", unit: "笔" },
  ];

  const matched = candidates.filter((candidate) => candidate.test.test(text));
  if (matched.length === 0) {
    return [
      {
        nameOriginal: "未拆分项目",
        nameNormalized: "未拆分项目",
        quantity: 1,
        unit: "笔",
        category: "uncategorized",
        priceOriginal: total,
      },
    ];
  }

  const split = total / matched.length;
  return matched.map((candidate) => ({
    nameOriginal: candidate.original,
    nameNormalized: candidate.normalized,
    quantity: 1,
    unit: candidate.unit,
    category: candidate.category,
    priceOriginal: split,
  }));
}

function extractOwnershipTags(text: string): OwnershipTag[] {
  const tags: OwnershipTag[] = [];
  if (/伴侣|女朋友|男朋友|partner/i.test(text)) tags.push("伴侣");
  if (/宠物|猫|狗|pet/i.test(text)) tags.push("宠物");
  if (/项目|uncut|clears?pend|api|openai/i.test(text)) tags.push("项目");
  if (tags.length === 0) tags.push("个人");
  return tags;
}

function parseTimeExpression(text: string) {
  const date = new Date();
  if (/前天/.test(text)) date.setDate(date.getDate() - 2);
  if (/昨天|昨晚/.test(text)) date.setDate(date.getDate() - 1);
  if (/昨晚/.test(text)) date.setHours(20, 0, 0, 0);
  return date.toISOString();
}

export function filterByWindow(transactions: TransactionRecord[], window: DashboardWindow) {
  const now = new Date();
  return transactions.filter((transaction) => {
    const happened = new Date(transaction.happenedAt);
    if (window === "today") return happened.toDateString() === now.toDateString();
    if (window === "month") return happened.getFullYear() === now.getFullYear() && happened.getMonth() === now.getMonth();
    const start = new Date(now);
    start.setDate(now.getDate() - 30);
    return happened >= start;
  });
}

export function filterTimeline(transactions: TransactionRecord[], filter: TimelineFilter) {
  const active = transactions.filter((transaction) => !transaction.deletedAt);
  if (filter === "all") return active;
  if (filter === "review") return active.filter((transaction) => transaction.pendingReview);
  return active.filter((transaction) => transaction.kind === filter);
}

export function metricsFor(transactions: TransactionRecord[]) {
  const active = transactions.filter((transaction) => !transaction.deletedAt);
  const income = active
    .filter((transaction) => transaction.kind === "income" || transaction.kind === "refund")
    .reduce((sum, transaction) => sum + transaction.amountPHP, 0);
  const expense = active
    .filter((transaction) => transaction.kind === "expense" || transaction.kind === "investment")
    .reduce((sum, transaction) => sum + transaction.amountPHP, 0);
  const transfer = active.filter((transaction) => transaction.kind === "transfer").reduce((sum, transaction) => sum + transaction.amountPHP, 0);
  return { netPHP: income - expense, incomePHP: income, expensePHP: expense, transferPHP: transfer };
}

export function generateInsights(transactions: TransactionRecord[], forecasts: ForecastRecord[]) {
  const active = transactions.filter((transaction) => !transaction.deletedAt);
  const pending = active.filter((transaction) => transaction.pendingReview);
  const large = active.find((transaction) => transaction.amountCNY >= 500);
  const food = active.filter((transaction) => transaction.lineItems.some((item) => item.category === "餐饮"));
  const latestForecast = forecasts[0];

  return [
    large ? `高风险：${large.merchantName} 单笔约 ¥${large.amountCNY.toFixed(0)}，已标记为重点关注。` : "",
    pending.length ? `${pending.length} 笔低自信记录待复核，系统会先保留但不强行打断你。` : "",
    food.length > 1 ? `外出就餐近期有 ${food.length} 次，后续会动态判断是否改善。` : "",
    latestForecast ? `${latestForecast.title}：预计 ₱${latestForecast.expectedAmountPHP.toLocaleString("en-US")}。` : "",
  ].filter(Boolean);
}

export function answerQuery(query: string, transactions: TransactionRecord[]) {
  const text = query.toLowerCase();
  const wantsAnswer = /多少|花了|消费|买了|总共|一共|how much|spent/.test(text);
  if (!wantsAnswer) return null;

  let scoped = transactions.filter((transaction) => !transaction.deletedAt);
  if (/上个月|last month/.test(text)) {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    scoped = scoped.filter((transaction) => {
      const date = new Date(transaction.happenedAt);
      return date.getFullYear() === lastMonth.getFullYear() && date.getMonth() === lastMonth.getMonth();
    });
  }
  if (/最近30天|30 days/.test(text)) scoped = filterByWindow(scoped, "rolling30");

  if (/水|矿泉水|瓶装水/.test(text)) {
    const hits = scoped.filter((transaction) => transaction.lineItems.some((item) => item.nameNormalized.includes("水")));
    const bottles = hits.reduce((sum, transaction) => {
      return sum + transaction.lineItems.filter((item) => item.nameNormalized.includes("水")).reduce((subtotal, item) => subtotal + item.quantity, 0);
    }, 0);
    const total = hits.reduce((sum, transaction) => sum + transaction.amountPHP, 0);
    return `你查到的水相关记录共 ${bottles || hits.length} 瓶/次，合计约 ₱${total.toFixed(2)}，约 ¥${convert(total, "PHP").cny.toFixed(2)}。`;
  }

  if (/711|7-eleven|7 eleven/.test(text)) {
    const hits = scoped.filter((transaction) => /7-?eleven|711/i.test(transaction.merchantName));
    const total = hits.reduce((sum, transaction) => sum + transaction.amountPHP, 0);
    return `你在 7-Eleven 相关记录里一共消费 ₱${total.toFixed(2)}，约 ¥${convert(total, "PHP").cny.toFixed(2)}，共 ${hits.length} 笔。`;
  }

  const total = scoped.reduce((sum, transaction) => sum + transaction.amountPHP, 0);
  return `当前范围内共有 ${scoped.length} 笔记录，合计 ₱${total.toFixed(2)}，约 ¥${convert(total, "PHP").cny.toFixed(2)}。`;
}

export function exportLedger(state: LedgerState) {
  const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
  return URL.createObjectURL(blob);
}
